import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Button, Alert, Icon, Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter, TextInput } from '@qtm/react2';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useLocation, useNavigate } from 'react-router';
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { buildSchemaRegistry, deriveBodySchemaFields } from '../utils/apimSchema';
import {
  SELECTED_SUBSCRIPTION_STORAGE_KEY,
  SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY,
  SUBSCRIPTION_SELECTED_EVENT,
  extractSubscriptionAccountId,
} from '../constants/subscriptions';
import { resolveSubscriptionToken } from './selfServicePrefill';
import BackButton from '../components/BackButton';

const DEFAULT_APIM_SCOPE = 'api://a34c14f8-6677-4c55-b49d-ccd17a6af4c9/.default';
const SUBSCRIPTION_REQUEST_URL = 'https://postit.service-now.com/esc?id=sc_cat_item&sys_id=7d0f842097f22510b1c6359fe153afd7';

function coerceStringArray(value, enumValues = [], required = false) {
  const normalizeEntry = (entry) => {
    if (entry === null || entry === undefined) {
      return '';
    }
    if (typeof entry === 'string') {
      return entry.trim();
    }
    try {
      return JSON.stringify(entry);
    } catch (err) {
      return String(entry);
    }
  };

  let result = [];

  if (Array.isArray(value)) {
    result = value.map(normalizeEntry).filter((entry) => entry !== '');
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          result = parsed.map(normalizeEntry).filter((entry) => entry !== '');
        } else {
          result = [trimmed];
        }
      } catch (err) {
        if (trimmed.includes(',')) {
          result = trimmed
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry !== '');
        } else {
          result = [trimmed];
        }
      }
    }
  } else if (value !== null && value !== undefined) {
    result = [normalizeEntry(value)].filter((entry) => entry !== '');
  }

  if ((result.length === 0) && required && Array.isArray(enumValues) && enumValues.length > 0) {
    const fallback = normalizeEntry(enumValues[0]);
    if (fallback) {
      result = [fallback];
    }
  }

  return result;
}

function extractOAuthScopesFromSpec(specification, operationId) {
  if (!specification || typeof specification !== 'object') {
    return [];
  }

  const securitySchemes = specification?.components?.securitySchemes;
  if (!securitySchemes || typeof securitySchemes !== 'object') {
    return [];
  }

  const normalizedOperationId = typeof operationId === 'string' ? operationId.trim() : '';
  const requirementList = [];

  if (normalizedOperationId) {
    const paths = specification.paths || {};
    outer: for (const pathItem of Object.values(paths)) {
      if (!pathItem || typeof pathItem !== 'object') continue;
      for (const [method, operation] of Object.entries(pathItem)) {
        if (!operation || typeof operation !== 'object') continue;
        if (['parameters', 'summary', 'description'].includes(method)) continue;
        const opId = typeof operation.operationId === 'string' ? operation.operationId : '';
        if (!opId) continue;
        if (opId === normalizedOperationId || opId.toLowerCase() === normalizedOperationId.toLowerCase()) {
          const opSecurity = Array.isArray(operation.security) ? operation.security : [];
          if (opSecurity.length > 0) {
            requirementList.push(...opSecurity);
          }
          break outer;
        }
      }
    }
  }

  if (requirementList.length === 0) {
    const documentSecurity = Array.isArray(specification.security) ? specification.security : [];
    if (documentSecurity.length > 0) {
      requirementList.push(...documentSecurity);
    }
  }

  const scopes = new Set();

  const addScopesFromScheme = (scheme) => {
    if (!scheme || typeof scheme !== 'object') {
      return;
    }
    const flows = scheme.flows;
    if (flows && typeof flows === 'object') {
      Object.values(flows).forEach((flow) => {
        const flowScopes = flow?.scopes;
        if (flowScopes && typeof flowScopes === 'object') {
          Object.keys(flowScopes).forEach((scope) => {
            if (typeof scope === 'string' && scope.trim()) {
              scopes.add(scope.trim());
            }
          });
        }
      });
    }

    const extScopes = scheme['x-ms-scopes'] || scheme['x-scopes'];
    if (Array.isArray(extScopes)) {
      extScopes.forEach((scope) => {
        if (typeof scope === 'string' && scope.trim()) {
          scopes.add(scope.trim());
        }
      });
    } else if (typeof extScopes === 'string') {
      extScopes.split(/[\s,]+/).forEach((scope) => {
        if (scope && scope.trim()) {
          scopes.add(scope.trim());
        }
      });
    }

    const audienceExt =
      scheme['x-azure-audience'] ||
      scheme['x-audience'] ||
      scheme['x-ms-audience'] ||
      scheme['x-ms-authorization-audience'];
    if (typeof audienceExt === 'string' && audienceExt.trim()) {
      const audience = audienceExt.trim().replace(/\/+$/, '');
      if (audience) {
        const scope = audience.toLowerCase().endsWith('/.default') ? audience : `${audience}/.default`;
        scopes.add(scope);
      }
    }
  };

  if (requirementList.length > 0) {
    requirementList.forEach((requirement) => {
      if (!requirement || typeof requirement !== 'object') {
        return;
      }
      Object.entries(requirement).forEach(([schemeName, requestedScopes]) => {
        const scheme = securitySchemes[schemeName];
        if (!scheme || typeof scheme !== 'object') {
          return;
        }
        const type = typeof scheme.type === 'string' ? scheme.type.toLowerCase() : '';
        if (type !== 'oauth2' && type !== 'openidconnect') {
          return;
        }
        const explicitScopes = Array.isArray(requestedScopes)
          ? requestedScopes.map((scope) => (typeof scope === 'string' ? scope.trim() : '')).filter(Boolean)
          : typeof requestedScopes === 'string'
            ? requestedScopes.split(/[\s,]+/).map((scope) => scope.trim()).filter(Boolean)
            : [];
        if (explicitScopes.length > 0) {
          explicitScopes.forEach((scope) => scopes.add(scope));
        } else {
          addScopesFromScheme(scheme);
        }
      });
    });
  }

  if (scopes.size === 0) {
    Object.values(securitySchemes).forEach((scheme) => {
      if (!scheme || typeof scheme !== 'object') {
        return;
      }
      const type = typeof scheme.type === 'string' ? scheme.type.toLowerCase() : '';
      if (type === 'oauth2' || type === 'openidconnect') {
        addScopesFromScheme(scheme);
      }
    });
  }

  return Array.from(scopes);
}

export default function SelfServicePage(showBackButton) {
  const { getToken, account } = useContext(AuthContext);
  const { darkMode } = useTheme();
  const { instance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [schemaFields, setSchemaFields] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState('');
  const [config, setConfig] = useState(null);
  const [operationMeta, setOperationMeta] = useState(null);
  const [apiContext, setApiContext] = useState({ api: null, subscriptionHeaderName: '', oauthScope: '', oauthScopes: [] });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionCheckError, setSubscriptionCheckError] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState(() => {
    if (typeof window === 'undefined') {
      return { name: '', accountId: '' };
    }
    try {
      const storedName = localStorage.getItem(SELECTED_SUBSCRIPTION_STORAGE_KEY) || '';
      const storedAccount = localStorage.getItem(SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY);
      const accountId = storedAccount || extractSubscriptionAccountId(storedName);
      return { name: storedName || '', accountId: accountId || '' };
    } catch (err) {
      console.warn('SelfServicePage: unable to read stored subscription info', err);
      return { name: '', accountId: '' };
    }
  });

  const apiCacheRef = useRef(new Map());
  const schemaCacheRef = useRef(new Map());
  const subscriptionKeyRef = useRef('');
  const suppressHashSelectionRef = useRef(false);

  const hashItemId = useMemo(() => {
    const rawHash = typeof location.hash === 'string' ? location.hash.replace(/^#/, '') : '';
    if (!rawHash) {
      return '';
    }
    try {
      return decodeURIComponent(rawHash.trim());
    } catch (err) {
      return rawHash.trim();
    }
  }, [location.hash]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handler = (event) => {
      const detail = event?.detail || {};
      setSubscriptionInfo({
        name: detail.subscriptionName || '',
        accountId: detail.accountId || '',
      });
    };
    window.addEventListener(SUBSCRIPTION_SELECTED_EVENT, handler);
    return () => {
      window.removeEventListener(SUBSCRIPTION_SELECTED_EVENT, handler);
    };
  }, []);

  const stringifyValue = useCallback((value) => {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map((entry) => {
        if (entry === null || entry === undefined) {
          return '';
        }
        if (typeof entry === 'string') {
          return entry;
        }
        try {
          return JSON.stringify(entry);
        } catch (err) {
          return String(entry);
        }
      });
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (err) {
      return String(value);
    }
  }, []);

  const userClaimValues = useMemo(() => {
    const map = new Map();
    const claims = account?.idTokenClaims;
    if (claims && typeof claims === 'object') {
      Object.entries(claims).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        const normalizedKey = key.toLowerCase();
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          map.set(normalizedKey, value);
        } else if (Array.isArray(value) && value.length > 0) {
          map.set(normalizedKey, value[0]);
        }
      });

      const emailList = claims.emails;
      if (Array.isArray(emailList) && emailList.length > 0) {
        const primary = emailList.find((entry) => typeof entry === 'string' && entry.trim());
        if (primary) {
          map.set('email', primary.trim());
        }
      }
    }

    if (typeof account?.username === 'string' && account.username.trim()) {
      const username = account.username.trim();
      map.set('preferred_username', username);
      if (!map.has('email') && username.includes('@')) {
        map.set('email', username);
      }
    }

    return map;
  }, [account]);

  const resolveClaimValue = useCallback((claimKey) => {
    if (!claimKey || typeof claimKey !== 'string') {
      return undefined;
    }
    const normalized = claimKey.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (userClaimValues.has(normalized)) {
      return userClaimValues.get(normalized);
    }
    return undefined;
  }, [userClaimValues]);

  const resolveSubscriptionValue = useCallback(
    (token) => resolveSubscriptionToken(token, subscriptionInfo),
    [subscriptionInfo]
  );

  const deriveInitialFormValues = useCallback((item, fields) => {
    const initial = {};
    const overlayMap = new Map((item?.fields || []).map((field) => [field.key, field]));

    fields.forEach((spec) => {
      const overlay = overlayMap.get(spec.name);
      const isArrayField = typeof spec.type === 'string' && spec.type.toLowerCase().startsWith('array');
      const enumValues = Array.isArray(spec.enumValues) ? spec.enumValues : [];

      if (isArrayField) {
        let candidate;
        if (overlay && overlay.const_value !== undefined) {
          candidate = overlay.const_value;
        } else if (overlay && overlay.prefill !== undefined) {
          candidate = overlay.prefill;
        } else if (overlay && overlay.prefillFromSubscription) {
          const subscriptionValue = resolveSubscriptionValue(overlay.prefillFromSubscription);
          if (subscriptionValue !== undefined && subscriptionValue !== null && subscriptionValue !== '') {
            candidate = subscriptionValue;
          }
        } else if (overlay && overlay.prefillFromClaim) {
          const claimValue = resolveClaimValue(overlay.prefillFromClaim);
          if (claimValue !== undefined) {
            candidate = claimValue;
          }
        } else if (spec.defaultValue !== undefined) {
          candidate = spec.defaultValue;
        } else if (enumValues.length > 0) {
          candidate = enumValues[0];
        } else {
          candidate = [];
        }
        initial[spec.name] = coerceStringArray(candidate, enumValues, spec.required);
        return;
      }

      let value = '';
      if (overlay) {
        if (overlay.const_value !== undefined) {
          value = stringifyValue(overlay.const_value);
        } else if (overlay.prefill !== undefined) {
          value = stringifyValue(overlay.prefill);
        }
      }
      if (!value && overlay?.prefillFromSubscription) {
        const subscriptionValue = resolveSubscriptionValue(overlay.prefillFromSubscription);
        if (subscriptionValue !== undefined && subscriptionValue !== null && subscriptionValue !== '') {
          value = stringifyValue(subscriptionValue);
        }
      }
      if (!value && overlay?.prefillFromClaim) {
        const claimValue = resolveClaimValue(overlay.prefillFromClaim);
        if (claimValue !== undefined && claimValue !== null) {
          value = stringifyValue(claimValue);
        }
      }
      if (!value && enumValues.length > 0) {
        value = enumValues[0];
      }
      initial[spec.name] = value;
    });

    (item?.fields || []).forEach((field) => {
      if (field.hidden) {
        return;
      }
      if (Object.prototype.hasOwnProperty.call(initial, field.key)) {
        return;
      }
      const isArrayField = typeof field?.schemaType === 'string' && field.schemaType.toLowerCase().startsWith('array');
      const enumValues = Array.isArray(field?.enumValues) ? field.enumValues : [];

      if (field.const_value !== undefined) {
        initial[field.key] = isArrayField
          ? coerceStringArray(field.const_value, enumValues, field.required)
          : stringifyValue(field.const_value);
        return;
      }
      if (field.prefill !== undefined) {
        initial[field.key] = isArrayField
          ? coerceStringArray(field.prefill, enumValues, field.required)
          : stringifyValue(field.prefill);
        return;
      }
      if (field.prefillFromSubscription) {
        const subscriptionValue = resolveSubscriptionValue(field.prefillFromSubscription);
        if (subscriptionValue !== undefined && subscriptionValue !== null && subscriptionValue !== '') {
          initial[field.key] = isArrayField
            ? coerceStringArray(subscriptionValue, enumValues, field.required)
            : stringifyValue(subscriptionValue);
          return;
        }
      }
      if (field.prefillFromClaim) {
        const claimValue = resolveClaimValue(field.prefillFromClaim);
        if (claimValue !== undefined && claimValue !== null) {
          initial[field.key] = isArrayField
            ? coerceStringArray(claimValue, enumValues, field.required)
            : stringifyValue(claimValue);
          return;
        }
      }
      initial[field.key] = isArrayField ? [] : '';
    });

    return initial;
  }, [stringifyValue, resolveClaimValue, resolveSubscriptionValue]);

  useEffect(() => {
    let cancelled = false;

    if (!account) {
      setConfig(null);
      return () => {
        cancelled = true;
      };
    }

    const loadConfig = async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Unable to acquire access token for config retrieval.');
        }
        const resp = await fetch('/api/auth-config', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        });
        if (!resp.ok) {
          throw new Error(`Config request failed (${resp.status})`);
        }
        const data = await resp.json();
        if (!cancelled) {
          setConfig(data || {});
        }
      } catch (err) {
        if (!cancelled) {
          console.error('SelfServicePage: failed to load config', err);
          setConfig(null);
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [getToken, account]);

  const fetchApiDetails = useCallback(async (apiId) => {
    if (!apiId) {
      throw new Error('Missing API identifier.');
    }
    if (apiCacheRef.current.has(apiId)) {
      return apiCacheRef.current.get(apiId);
    }
    const token = await getToken();
    const resp = await fetch(`/api/apim/apis/${encodeURIComponent(apiId)}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(text || `Unable to load API metadata (${resp.status})`);
    }
    const data = await resp.json();
    apiCacheRef.current.set(apiId, data);
    return data;
  }, [getToken]);

  const loadSchemaForItem = useCallback(async (item) => {
    if (!item || !item.api_id || !item.operation_id) {
      return { fields: [], operationMeta: null, api: null, subscriptionHeaderName: '', oauthScope: '' };
    }
    const cacheKey = `${item.api_id}::${item.operation_id}`;
    const cached = schemaCacheRef.current.get(cacheKey);
    if (cached) {
      return cached;
    }

    const apiDetails = await fetchApiDetails(item.api_id);
    const registry = buildSchemaRegistry(apiDetails?.specification);
    const operations = Array.isArray(apiDetails?.operations) ? apiDetails.operations : [];
    const operation = operations.find((op) => {
      const props = op?.properties || {};
      return props.operationId === item.operation_id || op?.name === item.operation_id;
    });

    if (!operation) {
      throw new Error('Operation metadata not found for self-service item.');
    }

    const opProps = operation?.properties || {};
    const requestRepresentations = Array.isArray(opProps?.request?.representations)
      ? opProps.request.representations
      : [];

    const fields = deriveBodySchemaFields({
      requestRepresentations,
      schemaRegistry: registry,
    });

    const subscriptionHeaderName = apiDetails?.api?.properties?.subscriptionKeyParameterNames?.header
      || apiDetails?.api?.properties?.subscriptionKeyParameterNames?.headerName
      || 'Ocp-Apim-Subscription-Key';
    const oauthScope = apiDetails?.api?.properties?.authenticationSettings?.oAuth2?.scope || '';

    const oauthScopesFromSpec = extractOAuthScopesFromSpec(apiDetails?.specification, item.operation_id);

    const result = {
      fields,
      operationMeta: {
        method: opProps.method || opProps.verb || 'POST',
        urlTemplate: opProps.urlTemplate || opProps.template || '',
        templateParameters: Array.isArray(opProps.templateParameters) ? opProps.templateParameters : [],
        queryParameters: Array.isArray(opProps?.request?.queryParameters) ? opProps.request.queryParameters : [],
      },
      api: apiDetails?.api || null,
      subscriptionHeaderName: typeof subscriptionHeaderName === 'string' ? subscriptionHeaderName : 'Ocp-Apim-Subscription-Key',
      oauthScope,
      oauthScopes: Array.isArray(oauthScopesFromSpec) ? oauthScopesFromSpec : [],
    };

    schemaCacheRef.current.set(cacheKey, result);
    return result;
  }, [fetchApiDetails]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = await getToken();
        const resp = await fetch('/api/selfservice/items', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
        if (!resp.ok) {
          throw new Error(`Failed to load self-service items (${resp.status})`);
        }
        const data = await resp.json();
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unable to retrieve self-service items.');
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;

    const checkSubscriptions = async () => {
      try {
        const token = await getToken();
        if (!token) {
          return;
        }
        const response = await fetch('/api/apim/subscriptions', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Subscription lookup failed (${response.status})`);
        }
        const body = await response.json();
        if (cancelled) {
          return;
        }
        const list = Array.isArray(body?.value) ? body.value : [];
        if (list.length === 0) {
          setSubscriptionModalOpen(true);
        } else {
          setSubscriptionModalOpen(false);
        }
        setSubscriptionCheckError('');
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.warn('SelfServicePage: subscription check failed', err);
        setSubscriptionCheckError(err?.message || 'Unable to verify subscriptions.');
      }
    };

    checkSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const apimBaseUrl = useMemo(() => {
    if (!config || typeof config.APIM_TRY_BASE_URL !== 'string') {
      return '';
    }
    return config.APIM_TRY_BASE_URL.trim();
  }, [config]);

  const apiGatewaySegment = useMemo(() => {
    const api = apiContext.api;
    if (!api) {
      return '';
    }
    const props = api?.properties || {};
    const segments = [];
    if (typeof props.path === 'string' && props.path.trim()) {
      segments.push(props.path.trim().replace(/^\/+|\/+$/g, ''));
    } else if (typeof api?.name === 'string' && api.name.trim()) {
      segments.push(api.name.trim().replace(/^\/+|\/+$/g, ''));
    }
    if (typeof props.apiVersion === 'string' && props.apiVersion.trim()) {
      segments.push(props.apiVersion.trim().replace(/^\/+|\/+$/g, ''));
    }
    return segments.filter(Boolean).join('/');
  }, [apiContext.api]);

  const overlayFields = useMemo(
    () => (Array.isArray(selectedItem?.fields) ? selectedItem.fields : []),
    [selectedItem]
  );

  const overlayFieldMap = useMemo(
    () => new Map(overlayFields.map((field) => [field.key, field])),
    [overlayFields]
  );

  const combinedFields = useMemo(() => {
    if (!selectedItem) {
      return [];
    }

    const normalizeKey = (value) => {
      if (typeof value !== 'string') return '';
      return value.trim();
    };

    const overlayList = Array.isArray(selectedItem.fields) ? selectedItem.fields : [];
    const overlayMap = new Map();

    overlayList.forEach((field) => {
      const key = normalizeKey(field?.key);
      if (!key) return;
      overlayMap.set(key, {
        ...field,
        key,
        label: field.label || key,
      });
    });

    const resultMap = new Map();

    if (Array.isArray(schemaFields) && schemaFields.length > 0) {
      schemaFields.forEach((spec) => {
        const key = normalizeKey(spec?.name);
        if (!key) return;

        const overlay = overlayMap.get(key);
        const merged = {
          key,
          label: overlay?.label || key,
          description: overlay?.description ?? spec.description,
          required: overlay?.required ?? spec.required,
          hidden: overlay?.hidden ?? false,
          const_value: overlay?.const_value,
          prefill: overlay?.prefill,
          prefillFromSubscription: overlay?.prefillFromSubscription,
          prefillFromClaim: overlay?.prefillFromClaim,
          locked: overlay?.locked,
          enumValues: overlay?.enumValues || overlay?.enum || spec.enumValues,
          constraints: spec.constraints,
          notes: spec.notes,
          schemaType: overlay?.schemaType || spec.type,
          itemsType: overlay?.itemsType || spec.itemsType,
        };

        resultMap.set(key, overlay ? { ...overlay, ...merged } : merged);
        overlayMap.delete(key);
      });
    }

    overlayMap.forEach((field, key) => {
      if (!resultMap.has(key)) {
        resultMap.set(key, field);
      }
    });

    return Array.from(resultMap.values());
  }, [selectedItem, schemaFields]);

  const uriFields = useMemo(() => {
    if (!operationMeta) {
      return [];
    }

    const serializeNotes = (existingNotes, location) => {
      const notes = Array.isArray(existingNotes) ? [...existingNotes] : [];
      notes.unshift(location === 'path' ? 'Location: path parameter' : 'Location: query parameter');
      return notes;
    };

    const normalizeParam = (param, location) => {
      const name = typeof param?.name === 'string' ? param.name.trim() : '';
      if (!name) return null;

      const overlay = overlayFieldMap.get(name);
      const type =
        overlay?.schemaType ||
        param?.type ||
        param?.dataType ||
        param?.valueType ||
        param?.schema?.type ||
        '';
      const itemsType = overlay?.itemsType || param?.schema?.items?.type || '';

      const description = overlay?.description ?? param?.description ?? '';

      const enumValues = overlay?.enumValues || overlay?.enum ||
        (Array.isArray(param?.values) ? param.values : Array.isArray(param?.allowedValues) ? param.allowedValues : []);

      const example =
        overlay?.example ||
        param?.example ||
        (param?.examples && typeof param.examples === 'object'
          ? param.examples.default?.value ?? param.examples?.value
          : undefined);

      const defaultValue = param?.defaultValue ?? param?.schema?.default;

      return {
        key: name,
        label: name,
        description,
        required: overlay?.required ?? param?.required ?? false,
        hidden: overlay?.hidden ?? false,
        const_value: overlay?.const_value,
        prefill: overlay?.prefill,
        prefillFromSubscription: overlay?.prefillFromSubscription,
        prefillFromClaim: overlay?.prefillFromClaim,
        locked: overlay?.locked,
        enumValues,
        schemaType: type,
        itemsType,
        notes: serializeNotes(overlay?.notes ?? param?.notes, location),
        example: example ?? defaultValue,
        defaultValue,
      };
    };

    const fields = [];

    if (Array.isArray(operationMeta.templateParameters)) {
      operationMeta.templateParameters.forEach((param) => {
        const normalized = normalizeParam(param, 'path');
        if (normalized) {
          fields.push(normalized);
        }
      });
    }

    if (Array.isArray(operationMeta.queryParameters)) {
      operationMeta.queryParameters.forEach((param) => {
        const normalized = normalizeParam(param, 'query');
        if (normalized) {
          fields.push(normalized);
        }
      });
    }

    return fields;
  }, [operationMeta, overlayFieldMap]);

  const uriFieldKeys = useMemo(() => new Set(uriFields.map((field) => field.key)), [uriFields]);

  const bodyFields = useMemo(() => {
    const base = combinedFields.length > 0
      ? combinedFields
      : Array.isArray(selectedItem?.fields) ? selectedItem.fields : [];

    if (!Array.isArray(base) || base.length === 0) {
      return [];
    }

    return base.filter((field) => field && !uriFieldKeys.has(field.key));
  }, [combinedFields, selectedItem, uriFieldKeys]);

  useEffect(() => {
    if (!selectedItem || bodyFields.length === 0) {
      return;
    }

    setFormValues((prev) => {
      let changed = false;
      const next = { ...prev };

      bodyFields.forEach((field) => {
        if (!field || field.hidden || !field.prefillFromSubscription) {
          return;
        }
        const subscriptionValue = resolveSubscriptionValue(field.prefillFromSubscription);
        if (subscriptionValue === undefined || subscriptionValue === null || subscriptionValue === '') {
          return;
        }

        const currentValue = next[field.key];
        const isArrayField = typeof field?.schemaType === 'string' && field.schemaType.toLowerCase().startsWith('array');

        if (isArrayField) {
          const coerced = coerceStringArray(subscriptionValue, Array.isArray(field.enumValues) ? field.enumValues : [], field.required);
          const normalized = Array.isArray(coerced) ? coerced : [coerced].filter((item) => item !== '');
          const currentArray = Array.isArray(currentValue) ? currentValue : [];
          if (normalized.length === 0) {
            return;
          }
          if (JSON.stringify(currentArray) === JSON.stringify(normalized)) {
            return;
          }
          next[field.key] = normalized;
          changed = true;
          return;
        }

        const serialized = stringifyValue(subscriptionValue);
        if (!serialized) {
          return;
        }
        if (typeof currentValue === 'string' && currentValue === serialized) {
          return;
        }
        next[field.key] = serialized;
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [bodyFields, resolveSubscriptionValue, selectedItem, stringifyValue]);

  const handleSelectItem = useCallback((item) => {
    setSelectedItem(item);
    setSchemaFields([]);
    setSchemaError('');
    setOperationMeta(null);
    setApiContext({ api: null, subscriptionHeaderName: '', oauthScope: '', oauthScopes: [] });
    setSubmitStatus('');
    setSubmitError('');
    setSubmitResult(null);
    setSubmitLoading(false);
    setFormValues(deriveInitialFormValues(item, []));
  }, [deriveInitialFormValues]);

  const handleCloseModal = useCallback(() => {
    suppressHashSelectionRef.current = true;
    setSelectedItem(null);
    setFormValues({});
    if (location.hash) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    } else {
      suppressHashSelectionRef.current = false;
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!hashItemId || items.length === 0) {
      return;
    }
    if (suppressHashSelectionRef.current) {
      if (!location.hash) {
        suppressHashSelectionRef.current = false;
      }
      return;
    }
    const normalizedTarget = hashItemId.trim().toLowerCase();
    if (!normalizedTarget) {
      return;
    }
    if (selectedItem && typeof selectedItem.id === 'string') {
      const normalizedSelected = selectedItem.id.trim().toLowerCase();
      if (normalizedSelected === normalizedTarget) {
        return;
      }
    }
    const match = items.find((item) => {
      const id = typeof item?.id === 'string' ? item.id.trim() : '';
      return id && id.toLowerCase() === normalizedTarget;
    });
    if (match) {
      handleSelectItem(match);
    }
  }, [hashItemId, items, selectedItem, handleSelectItem, location.hash]);

  useEffect(() => {
    if (!selectedItem || typeof selectedItem.id !== 'string') {
      return;
    }
    const trimmedId = selectedItem.id.trim();
    if (!trimmedId) {
      return;
    }
    const targetHash = `#${encodeURIComponent(trimmedId)}`;
    if (location.hash === targetHash) {
      return;
    }
    navigate(`${location.pathname}${location.search}${targetHash}`, { replace: true });
  }, [selectedItem, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedItem) {
      return () => { cancelled = true; };
    }

    const loadSchema = async () => {
      setSchemaLoading(true);
      setSchemaError('');
      try {
        const { fields, operationMeta: meta, api, subscriptionHeaderName, oauthScope, oauthScopes } = await loadSchemaForItem(selectedItem);
        if (cancelled) {
          return;
        }
        setSchemaFields(fields);
        setOperationMeta(meta);
        setApiContext({
          api,
          subscriptionHeaderName,
          oauthScope,
          oauthScopes: Array.isArray(oauthScopes) ? oauthScopes : [],
        });
        setFormValues(deriveInitialFormValues(selectedItem, fields));
      } catch (err) {
        if (cancelled) {
          return;
        }
        setSchemaFields([]);
        setOperationMeta(null);
        setApiContext({ api: null, subscriptionHeaderName: '', oauthScope: '', oauthScopes: [] });
        setSchemaError(err?.message || 'Unable to derive request schema.');
        setFormValues(deriveInitialFormValues(selectedItem, []));
      } finally {
        if (!cancelled) {
          setSchemaLoading(false);
        }
      }
    };

    loadSchema();

    return () => {
      cancelled = true;
    };
  }, [selectedItem, loadSchemaForItem, deriveInitialFormValues]);

  useEffect(() => {
    if (!selectedItem) {
      return;
    }
    const targetFields = [...uriFields, ...bodyFields];
    if (targetFields.length === 0) {
      return;
    }

    setFormValues((prev) => {
      let changed = false;
      const next = { ...prev };

      targetFields.forEach((field) => {
        const key = field?.key;
        if (!key) {
          return;
        }
        if (field.hidden) {
          return;
        }
        const currentValue = next[key];
        if (currentValue !== undefined && currentValue !== '') {
          return;
        }

        const pickValue = (source) => {
          if (source === undefined || source === null) {
            return '';
          }
          return stringifyValue(source);
        };

        let value = '';
        if (field.const_value !== undefined) {
          value = pickValue(field.const_value);
        } else if (field.prefill !== undefined) {
          value = pickValue(field.prefill);
        } else if (field.prefillFromSubscription) {
          const subscriptionValue = resolveSubscriptionValue(field.prefillFromSubscription);
          if (subscriptionValue !== undefined && subscriptionValue !== null && subscriptionValue !== '') {
            value = pickValue(subscriptionValue);
          }
        } else if (field.prefillFromClaim) {
          const claimValue = resolveClaimValue(field.prefillFromClaim);
          if (claimValue !== undefined && claimValue !== null) {
            value = pickValue(claimValue);
          }
        } else if (field.defaultValue !== undefined) {
          value = pickValue(field.defaultValue);
        } else if (field.enumValues && field.enumValues.length > 0) {
          value = pickValue(field.enumValues[0]);
        } else if (field.example !== undefined) {
          value = pickValue(field.example);
        }

        if (value !== '') {
          if (Array.isArray(value)) {
            next[key] = value;
          } else if (typeof value === 'string' && value.trim()) {
            if ((field.schemaType || '').toLowerCase().startsWith('array')) {
              try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                  next[key] = parsed.map((entry) => (typeof entry === 'string' ? entry : String(entry)));
                } else {
                  next[key] = value;
                }
              } catch (err) {
                if (value.includes(',')) {
                  next[key] = value.split(',').map((item) => item.trim()).filter(Boolean);
                } else {
                  next[key] = [value];
                }
              }
            } else {
              next[key] = value;
            }
          } else {
            next[key] = value;
          }
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [selectedItem, uriFields, bodyFields, stringifyValue, resolveClaimValue, resolveSubscriptionValue]);

  const renderField = (field) => {
    if (field.hidden) {
      return null;
    }
    const value = formValues[field.key] ?? '';
    const enumOptions = Array.isArray(field.enumValues) ? field.enumValues : [];
    const hasEnum = enumOptions.length > 0;
    const helper = field.const_value
      ? 'Const value enforced server-side'
      : field.prefillFromSubscription
        ? `Prefilled from subscription ${field.prefillFromSubscription}`
      : field.prefillFromClaim
        ? `Prefilled from ${field.prefillFromClaim} claim`
        : field.locked
          ? 'Managed by server'
          : '';
    const metaLines = [];
    if (field.schemaType) {
      metaLines.push(`Type: ${field.schemaType}`);
    }
    if (hasEnum) {
      metaLines.push(`Allowed: ${enumOptions.join(', ')}`);
    }
    if (Array.isArray(field.constraints) && field.constraints.length > 0) {
      metaLines.push(field.constraints.join(', '));
    }
    if (Array.isArray(field.notes) && field.notes.length > 0) {
      metaLines.push(field.notes.join(' • '));
    }
    const disabled = Boolean(field.locked || field.const_value !== undefined);

    return (
      
      <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontWeight: 600 }}>
          {field.label || field.key}
          {field.required ? ' *' : ''}
        </span>
       
        {(() => {
          const isArrayField = (field.schemaType || '').toLowerCase().startsWith('array');
          const arrayItemsType = field.schemaType?.match(/^array<(.+)>$/i)?.[1]?.toLowerCase() || field.itemsType;

          if (isArrayField && (arrayItemsType === 'string' || !arrayItemsType)) {
            return (
              <ArrayStringField
                field={field}
                value={value}
                disabled={disabled}
                enumOptions={enumOptions}
                required={field.required}
                onChange={(nextArray) => {
                  setFormValues((prev) => ({
                    ...prev,
                    [field.key]: nextArray,
                  }));
                }}
              />
            );
          }

          if (hasEnum) {
            return (
              <select
                value={value}
                disabled={disabled}
                onChange={(event) => {
                  const nextValue = event?.target?.value ?? '';
                  setFormValues((prev) => ({
                    ...prev,
                    [field.key]: nextValue,
                  }));
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(148,163,184,0.6)',
                  background: disabled ? 'rgba(148,163,184,0.15)' : 'inherit',
                }}
              >
                {!field.required && <option value="">—</option>}
                {enumOptions.map((option) => {
                  const optionLabel = typeof option === 'string' ? option : String(option);
                  return (
                    <option key={optionLabel} value={optionLabel}>
                      {optionLabel}
                    </option>
                  );
                })}
              </select>
            );
          }

          return (
            <TextInput
              size="medium"
              value={value}
              disabled={disabled}
              onChange={(event) => {
                const nextValue = event?.target?.value ?? '';
                setFormValues((prev) => ({
                  ...prev,
                  [field.key]: nextValue,
                }));
              }}
              placeholder={(() => {
                if (field.const_value !== undefined) {
                  return typeof field.const_value === 'string'
                    ? field.const_value
                    : 'Constant value';
                }
                if (field.prefill !== undefined) {
                  return typeof field.prefill === 'string'
                    ? field.prefill
                    : 'Prefilled value';
                }
                const example = field.example || (Array.isArray(field.examples) ? field.examples[0] : undefined);
                if (example !== undefined) {
                  return String(example);
                }
                if (hasEnum) {
                  const first = enumOptions[0];
                  return typeof first === 'string' ? first : String(first);
                }
                if (field.schemaType === 'integer' || field.schemaType === 'number') {
                  return 'e.g. 42';
                }
                if (field.schemaType === 'boolean') {
                  return 'true or false';
                }
                if (field.schemaType === 'array') {
                  return '[ "value" ]';
                }
                if (field.schemaType === 'object') {
                  return '{ "key": "value" }';
                }
                return 'Enter a value';
              })()}
            />
          );
        })()}
        {field.description && (
          <Typography
            variant="paragraph-small"
            fontSize="0.78rem"
            classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-500'}
          >
            {field.description}
          </Typography>
        )}
        {helper && (
          <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
            {helper}
          </Typography>
        )}
        {metaLines.length > 0 && (
          <Typography
            variant="paragraph-small"
            style={{
              opacity: 0.75,
              fontStyle: 'italic',
            }}
          >
            <span style={{ color: darkMode ? '#94a3b8' : '#64748b' }}>
              {metaLines.join(' • ')}
            </span>
          </Typography>
        )}
      </label>
    );
  };

  const contentBackground = darkMode ? '#0f172a' : '#ffffff';

  const coerceValueForSubmission = useCallback((rawValue) => {
    if (rawValue === null || rawValue === undefined) {
      return undefined;
    }
    if (typeof rawValue !== 'string') {
      return rawValue;
    }
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return '';
    }
    if (/^(true|false)$/i.test(trimmed)) {
      return trimmed.toLowerCase() === 'true';
    }
    if (/^null$/i.test(trimmed)) {
      return null;
    }
    if (/^[-+]?\d+(\.\d+)?$/.test(trimmed)) {
      return trimmed;
    }
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch (err) {
        console.warn('SelfServicePage: unable to parse JSON value', err);
      }
    }
    return trimmed;
  }, []);

  const buildRequestBody = useCallback(() => {
    if (!selectedItem) {
      return null;
    }
    const specFields = Array.isArray(schemaFields) ? schemaFields : [];
    const keys = new Set();
    specFields.forEach((field) => {
      if (field?.name) {
        keys.add(field.name);
      } else if (field?.key) {
        keys.add(field.key);
      }
    });
    overlayFields.forEach((field) => {
      if (field?.key) {
        keys.add(field.key);
      }
    });

    if (keys.size === 0) {
      return null;
    }

    const body = {};
    keys.forEach((key) => {
      if (!key) {
        return;
      }
      const overlay = overlayFieldMap.get(key);
      if (overlay && overlay.const_value !== undefined) {
        body[key] = overlay.const_value;
        return;
      }
      if (overlay && overlay.prefill !== undefined && (formValues[key] === undefined || formValues[key] === '')) {
        body[key] = overlay.prefill;
        return;
      }
      const rawValue = formValues[key];
      if (rawValue === undefined || rawValue === '') {
        return;
      }
      const coerced = coerceValueForSubmission(rawValue);
      if (Array.isArray(coerced)) {
        const sanitizedArray = coerced
          .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
          .filter((entry) => entry !== undefined && entry !== null && String(entry).trim() !== '');
        if (sanitizedArray.length > 0) {
          body[key] = sanitizedArray;
        }
        return;
      }
      if (coerced !== undefined && coerced !== '') {
        body[key] = coerced;
      }
    });

    const pathKeys = Array.isArray(operationMeta?.templateParameters)
      ? operationMeta.templateParameters
          .map((param) => (typeof param?.name === 'string' ? param.name.trim() : ''))
          .filter(Boolean)
      : [];

    const queryKeys = Array.isArray(operationMeta?.queryParameters)
      ? operationMeta.queryParameters
          .map((param) => (typeof param?.name === 'string' ? param.name.trim() : ''))
          .filter(Boolean)
      : [];

    [...pathKeys, ...queryKeys].forEach((name) => {
      if (name) {
        delete body[name];
      }
    });

    return Object.keys(body).length > 0 ? body : null;
  }, [selectedItem, schemaFields, overlayFields, overlayFieldMap, formValues, coerceValueForSubmission, operationMeta]);

  const buildOperationUrl = useCallback(() => {
    if (!operationMeta) {
      return { url: '', missing: [] };
    }
    const base = apimBaseUrl ? apimBaseUrl.replace(/\/+$/, '') : '';
    if (!base) {
      return { url: '', missing: [] };
    }
    const segment = apiGatewaySegment ? `/${apiGatewaySegment.replace(/^\/+/, '')}` : '';
    const templateRaw = typeof operationMeta.urlTemplate === 'string' ? operationMeta.urlTemplate : '';
    const template = templateRaw.startsWith('/') ? templateRaw : `/${templateRaw}`;
    const requiredSet = new Set(
      Array.isArray(operationMeta.templateParameters)
        ? operationMeta.templateParameters
            .filter((param) => param?.required)
            .map((param) => (typeof param?.name === 'string' ? param.name.trim() : ''))
            .filter(Boolean)
        : []
    );

    const resolveParam = (name) => {
      if (!name) return undefined;
      const stored = formValues[name];
      if (Array.isArray(stored)) {
        const nonEmpty = stored.filter((entry) => entry !== undefined && entry !== null && String(entry).trim() !== '');
        if (nonEmpty.length > 0) {
          return nonEmpty[0];
        }
      } else if (stored !== undefined && stored !== '') {
        return stored;
      }
      const overlay = overlayFieldMap.get(name);
      if (overlay) {
        if (overlay.const_value !== undefined) {
          return overlay.const_value;
        }
        if (overlay.prefill !== undefined) {
          return overlay.prefill;
        }
      }
      return undefined;
    };

    const missing = [];
    const expanded = template.replace(/\{([^}]+)\}/g, (_, rawKey) => {
      const key = rawKey.trim();
      if (!key) {
        return '';
      }
      const value = resolveParam(key);
      if (value === undefined || value === null || value === '') {
        if (requiredSet.has(key)) {
          missing.push(key);
        }
        return '';
      }
      return encodeURIComponent(typeof value === 'string' ? value : String(value));
    });

    const pathUrl = `${base}${segment}${expanded}`.replace(/([^:]\/)\+/g, '$1/');

    const queryParameters = Array.isArray(operationMeta.queryParameters) ? operationMeta.queryParameters : [];
    const queryPairs = [];
    queryParameters.forEach((param) => {
      const name = typeof param?.name === 'string' ? param.name.trim() : '';
      if (!name) {
        return;
      }
      const stored = formValues[name];
      const collectValues = () => {
        if (Array.isArray(stored)) {
          return stored
            .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
            .filter((entry) => entry !== undefined && entry !== null && String(entry).trim() !== '');
        }
        if (stored === undefined || stored === null || stored === '') {
          return [];
        }
        return [stored];
      };
      const values = collectValues();
      if (values.length === 0) {
        if (param?.required) {
          missing.push(name);
        }
        return;
      }
      values.forEach((value) => {
        const stringValue = typeof value === 'string' ? value : String(value);
        queryPairs.push(`${encodeURIComponent(name)}=${encodeURIComponent(stringValue)}`);
      });
    });

    const url = queryPairs.length > 0 ? `${pathUrl}?${queryPairs.join('&')}` : pathUrl;
    return { url, missing };
  }, [operationMeta, apimBaseUrl, apiGatewaySegment, formValues, overlayFieldMap]);

  const ensureSubscriptionKey = useCallback(async () => {
    if (subscriptionKeyRef.current) {
      return subscriptionKeyRef.current;
    }
    try {
      const portalToken = await getToken();
      if (!portalToken) {
        return '';
      }
      const resp = await fetch('/api/apim/subscriptions', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${portalToken}`,
        },
      });
      if (!resp.ok) {
        console.warn('SelfServicePage: subscription list request failed', resp.status);
        return '';
      }
      const payload = await resp.json();
      const entries = Array.isArray(payload?.value) ? payload.value : [];
      const defaultEntry = entries.find((entry) => typeof entry?.primaryKey === 'string' && entry.primaryKey);
      const fallbackEntry = entries.find((entry) => typeof entry?.secondaryKey === 'string' && entry.secondaryKey);
      const key = defaultEntry?.primaryKey || fallbackEntry?.secondaryKey || '';
      if (key) {
        subscriptionKeyRef.current = key;
      }
      return key;
    } catch (err) {
      console.warn('SelfServicePage: failed to load subscription key', err);
      return '';
    }
  }, [getToken]);

  const acquireGatewayToken = useCallback(async () => {
    const scopeCandidates = Array.isArray(apiContext.oauthScopes) && apiContext.oauthScopes.length > 0
      ? apiContext.oauthScopes
      : [apiContext.oauthScope || DEFAULT_APIM_SCOPE];

    const scopes = scopeCandidates
      .flatMap((entry) => {
        if (typeof entry === 'string') {
          return entry.split(/[\s,]+/).map((scope) => scope.trim()).filter(Boolean);
        }
        return [];
      })
      .filter(Boolean);

    if (scopes.length === 0 || !instance) {
      return '';
    }

    const uniqueScopes = Array.from(new Set(scopes));
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
    try {
      const result = await instance.acquireTokenSilent({ scopes: uniqueScopes, account: account || undefined });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError || err?.errorCode === 'interaction_required') {
        try {
          const popupResult = await instance.acquireTokenPopup({ scopes: uniqueScopes, account: account || undefined });
          return popupResult.accessToken;
        } catch (popupErr) {
          console.warn('SelfServicePage: popup token acquisition failed', popupErr);
          return '';
        }
      }
      console.warn('SelfServicePage: silent token acquisition failed', err);
      return '';
    }
  }, [apiContext.oauthScopes, apiContext.oauthScope, instance]);

  const handleSubmit = useCallback(async () => {
    if (!selectedItem || !operationMeta) {
      return;
    }
    setSubmitStatus('');
    setSubmitError('');
    setSubmitResult(null);

    if (!apimBaseUrl) {
      setSubmitError('APIM gateway configuration is missing.');
      return;
    }

    const { url, missing } = buildOperationUrl();
    if (!url) {
      setSubmitError('Unable to resolve operation endpoint URL.');
      return;
    }
    if (missing.length > 0) {
      setSubmitError(`Missing required parameters: ${missing.join(', ')}`);
      return;
    }

    const payload = buildRequestBody();
    const method = (operationMeta.method || 'POST').toUpperCase();

    setSubmitLoading(true);
    try {
      const headers = new Headers();
      headers.set('Accept', 'application/json');

      if (payload && !['GET', 'HEAD'].includes(method)) {
        headers.set('Content-Type', 'application/json');
      }

      const subscriptionKey = await ensureSubscriptionKey();
      if (subscriptionKey && apiContext.subscriptionHeaderName) {
        headers.set(apiContext.subscriptionHeaderName, subscriptionKey);
      }

      const gatewayToken = await acquireGatewayToken();
      if (gatewayToken) {
        headers.set('Authorization', `Bearer ${gatewayToken}`);
      }

      const response = await fetch(url, {
        method,
        headers,
        body: payload && !['GET', 'HEAD'].includes(method) ? JSON.stringify(payload) : undefined,
      });

      const rawText = await response.text();
      let parsed;
      if (!rawText) {
        parsed = '';
      } else {
        try {
          parsed = JSON.parse(rawText);
        } catch (err) {
          parsed = rawText;
        }
      }

      setSubmitResult({
        status: response.status,
        ok: response.ok,
        body: parsed,
        headers: Array.from(response.headers.entries()),
      });

      if (!response.ok) {
        const errMessage = typeof parsed === 'string' && parsed
          ? parsed
          : `Gateway responded with ${response.status}`;
        throw new Error(errMessage);
      }

      setSubmitStatus('Request sent successfully.');
    } catch (err) {
      setSubmitError(err?.message || 'Failed to execute self-service action.');
    } finally {
      setSubmitLoading(false);
    }
  }, [selectedItem, operationMeta, apimBaseUrl, buildOperationUrl, buildRequestBody, ensureSubscriptionKey, apiContext.subscriptionHeaderName, acquireGatewayToken]);

  const handleOpenSubscriptionRequest = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.open(SUBSCRIPTION_REQUEST_URL, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        {showBackButton && <BackButton />}
      </div>
      <div>
        <Typography variant="heading-4" classes={darkMode ? 'text-bluegrey-50' : 'text-bluegrey-900'}>
          Self Service
        </Typography>
        <Typography
          variant="paragraph-small"
          fontSize="0.82rem"
          fontWeight="500"
          classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-500'}
        >
          Execute pre-approved operational actions without leaving the portal.
        </Typography>
      </div>

      {loading && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Icon icon="autorenew" />
          <Typography>Loading catalog…</Typography>
        </div>
      )}

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelectItem(item)}
              style={{
                textAlign: 'left',
                padding: '16px',
                borderRadius: 12,
                border: selectedItem?.id === item.id
                  ? '2px solid rgba(37, 99, 235, 0.65)'
                  : '1px solid rgba(148,163,184,0.35)',
                background: selectedItem?.id === item.id
                  ? (darkMode ? 'rgba(37,99,235,0.18)' : 'rgba(37,99,235,0.08)')
                  : contentBackground,
                color: darkMode ? '#edf2ff' : '#0f172a',
                cursor: 'pointer'
              }}
            >
              <Typography variant="heading-6">{item.title}</Typography>
              {item.description && (
                <Typography
                  variant="paragraph-small"
                  fontSize="0.78rem"
                  classes={darkMode ? 'text-bluegrey-300' : 'text-bluegrey-500'}
                  style={{ marginTop: 6 }}
                >
                  {item.description}
                </Typography>
              )}
            </button>
          ))}
          {items.length === 0 && (
            <div style={{ padding: 24, borderRadius: 12, border: '1px dashed rgba(148,163,184,0.4)' }}>
              <Typography>No self-service items available for your account.</Typography>
            </div>
          )}
        </div>
      )}

      {selectedItem && (
        <Modal open onCloseModal={handleCloseModal}>
          <ModalHeader>
            <ModalTitle>
              <Typography variant="heading-6">{selectedItem.title}</Typography>
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            {selectedItem.description && (
              <Typography variant="paragraph-small" style={{ opacity: 0.75, marginBottom: 12 }}>
                {selectedItem.description}
              </Typography>
            )}
            {schemaLoading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <Icon icon="autorenew" />
                <Typography>Loading request schema…</Typography>
              </div>
            )}
            {schemaError && (
              <Alert severity="warning" onClose={() => setSchemaError('')}>
                {schemaError}
              </Alert>
            )}
            {uriFields.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 6 }}>
                <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
                  URI parameters
                </Typography>
                {uriFields.map(renderField)}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {bodyFields.map(renderField)}
            </div>
            {submitError && (
              <Alert severity="error" onClose={() => setSubmitError('')}>
                {submitError}
              </Alert>
            )}
            {submitStatus && (
              <Alert severity="success" onClose={() => setSubmitStatus('')}>
                {submitStatus}
              </Alert>
            )}
            {submitResult && (
              <div style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 10,
                border: '1px solid rgba(148,163,184,0.35)',
                background: darkMode ? 'rgba(15,23,42,0.6)' : 'rgba(241,245,249,0.6)'
              }}>
                <Typography variant="paragraph-small" style={{ fontWeight: 600, marginBottom: 8 }}>
                  Response status: {submitResult.status} {submitResult.ok ? '(OK)' : '(Error)'}
                </Typography>
                {submitResult.headers && submitResult.headers.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
                      Headers
                    </Typography>
                    <pre style={{
                      margin: 0,
                      padding: '8px',
                      background: darkMode ? '#0f172a' : '#f9fafb',
                      borderRadius: 8,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                    }}>
{submitResult.headers.map(([key, value]) => `${key}: ${value}`).join('\n')}
                    </pre>
                  </div>
                )}
                {submitResult.body !== undefined && (
                  <div>
                    <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
                      Response body
                    </Typography>
                    <pre style={{
                      margin: 0,
                      padding: '8px',
                      background: darkMode ? '#0f172a' : '#f9fafb',
                      borderRadius: 8,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                    }}>
{typeof submitResult.body === 'string' ? submitResult.body : JSON.stringify(submitResult.body, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter classes="justify-end">
            <Button
              variant="primary"
              onClickEvent={handleSubmit}
              disabled={schemaLoading || submitLoading}
            >
              {submitLoading ? 'Submitting…' : 'Submit'}
            </Button>
            <Button variant="ghost" onClickEvent={handleCloseModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {subscriptionModalOpen && (
        <Modal open onCloseModal={() => setSubscriptionModalOpen(false)}>
          <ModalHeader>
            <ModalTitle>
              <Typography variant="heading-6">API subscription required</Typography>
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Typography style={{ marginBottom: 12 }}>
              To benefit from automation and self-service features, you need to subscribe to the TrustNest API Management (APIM) product and obtain a valid subscription key associated with your TDFaccountID.
            </Typography>
            <Typography style={{ marginBottom: 12 }}>
              You can request or activate your subscription through the ServiceNow catalog item
              {' '}
              <a
                href={SUBSCRIPTION_REQUEST_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                TrustNest APIM subscription request
              </a>
              , or contact the TrustNest team for assistance.
            </Typography>
            {subscriptionCheckError && (
              <Alert severity="warning" onClose={() => setSubscriptionCheckError('')} style={{ marginTop: 12 }}>
                {subscriptionCheckError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter classes="justify-between">
            <Button variant="primary" onClickEvent={handleOpenSubscriptionRequest}>
              Request subscription
            </Button>
            <Button variant="ghost" onClickEvent={() => setSubscriptionModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}
function ArrayStringField({ field, value, disabled, onChange, enumOptions = [], required }) {
  const list = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
    }
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
        }
      } catch (err) {
        if (value.includes(',')) {
          return value.split(',').map((item) => item.trim()).filter(Boolean);
        }
        return [value];
      }
    }
    return [];
  }, [value]);

    const normalizedEnums = useMemo(() => {
    if (!Array.isArray(enumOptions)) {
      return [];
    }
    const mapped = enumOptions.map((item) => (typeof item === 'string' ? item : String(item)));
    if (mapped.length === 0) {
      return mapped;
    }
    const defaultValue =
      typeof field.defaultValue === 'string' && field.defaultValue.trim()
        ? field.defaultValue.trim()
        : null;
    const includeDefault =
      defaultValue && mapped.includes(defaultValue) ? mapped : mapped.concat(defaultValue);
    const sorted = includeDefault.slice().filter(Boolean);
    
    if (defaultValue && !mapped.includes(defaultValue)) {
      sorted.sort((a, b) => (a === defaultValue ? -1 : b === defaultValue ? 1 : 0));
    }
    return sorted;
  }, [enumOptions, field.defaultValue]);

  const useSelect = normalizedEnums.length > 0;

  const displayValues = useMemo(() => {
    if (list.length === 0) {
      if (useSelect) {
        return required && normalizedEnums.length > 0 ? [normalizedEnums[0]] : [''];
      }
      return [''];
    }
    return list;
  }, [list, useSelect, normalizedEnums, required]);

  const handleItemChange = useCallback((index, nextValue) => {
    const sanitized = useSelect ? nextValue : nextValue?.trim() ?? '';
    const base = displayValues.map((item, idx) => (idx === index ? sanitized : item));
    onChange(base);
  }, [displayValues, onChange, useSelect]);

  const handleAddItem = useCallback(() => {
    if (useSelect) {
      const nextValue = normalizedEnums[0] ?? '';
      onChange([...displayValues, nextValue]);
    } else {
      onChange([...displayValues, '']);
    }
  }, [displayValues, onChange, useSelect, normalizedEnums]);

  const handleRemoveItem = useCallback((index) => {
    const filtered = displayValues.filter((_, idx) => idx !== index);
    onChange(filtered);
  }, [displayValues, onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {displayValues.map((item, index) => (
        <div
          key={`${field.key}-item-${index}`}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          {useSelect ? (
            <select
              value={item}
              disabled={disabled}
              onChange={(event) => handleItemChange(index, event?.target?.value ?? '')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid rgba(148,163,184,0.6)',
                background: disabled ? 'rgba(148,163,184,0.15)' : 'inherit',
              }}
            >
              {!required && (
                <option value="">
                  —
                </option>
              )}
              {normalizedEnums.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <TextInput
              size="medium"
              value={item}
              disabled={disabled}
              onChange={(event) => handleItemChange(index, event?.target?.value ?? '')}
              placeholder={`Value ${index + 1}`}
              style={{ flex: 1 }}
            />
          )}
          <Button
            variant="ghost"
            onClickEvent={() => handleRemoveItem(index)}
            disabled={disabled}
          >
            Remove
          </Button>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outline"
          size="small"
          onClickEvent={handleAddItem}
          disabled={disabled}
        >
          Add value
        </Button>
      </div>
    </div>
  );
}
